import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import React from 'react';

interface Column {
  title: string;
  dataIndex: string;
  key: string;
  width: string;
  align?: 'left' | 'right' | 'center';
  render?: (_row: any) => React.ReactNode;
}

interface ScrollableTableProps {
  columns: Column[];
  data: any[];
  rowKey: string | ((_row: any) => string);
  noDataText: string;
}

// DS table — mockup `.table` spec (design-guidelines.md §4):
// surface wrap r-lg border; uppercase xs/600 subtle sticky thead bg-canvas; row hover bg-canvas.
const ScrollableTable: React.FC<ScrollableTableProps> = ({ columns, data, rowKey, noDataText }) => {
  const totalWidth = columns.reduce((sum, col) => sum + parseInt(col.width.replace('px', '')), 0);

  return (
    <Box
      sx={{
        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--chakra-colors-border-strong)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-corner': { background: 'transparent' },
      }}
      overflow='auto'
      minW='100%'
      maxW='100%'
      h='100%'
      maxH='100%'
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      borderRadius='lg'
    >
      <Table minW={`${totalWidth}px`} style={{ tableLayout: 'fixed' }}>
        <Thead pos='sticky' zIndex={1} top={0}>
          <Tr>
            {columns.map((col) => (
              <Th
                key={col.key}
                w={col.width}
                py={{ base: '0.5rem', lg: '0.75rem' }}
                color='text.subtle'
                fontSize='xs'
                fontWeight={600}
                letterSpacing='0.04em'
                textAlign={col.align || 'left'}
                textTransform='uppercase'
                bg='bg.canvas'
                borderBottomWidth='1px'
                borderBottomColor='border.default'
                whiteSpace='nowrap'
              >
                {col.title}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data && data.length > 0 ? (
            data.map((row, index) => (
              <Tr
                key={typeof rowKey === 'function' ? rowKey(row) : rowKey ? row[rowKey] : index}
                sx={{ '&:last-of-type td': { borderBottom: 'none' } }}
                _hover={{ bg: 'bg.canvas' }}
                transition='background 0.12s ease-out'
              >
                {columns.map((col) => (
                  <Td
                    key={col.key}
                    w={col.width}
                    py={{ base: '0.75rem', lg: '1rem' }}
                    color='text.default'
                    fontSize={14}
                    textAlign={col.align || 'left'}
                    borderBottomWidth='1px'
                    borderBottomColor='border.default'
                    whiteSpace='nowrap'
                  >
                    {col.render ? (
                      col.render(row)
                    ) : (
                      <Text color='text.default'>{row[col.dataIndex]}</Text>
                    )}
                  </Td>
                ))}
              </Tr>
            ))
          ) : (
            <Tr>
              <Td py='1.5rem' color='text.muted' textAlign='center' colSpan={columns.length}>
                {noDataText}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

export default ScrollableTable;
